import type { FunctionComponent } from 'react';
import React, { Suspense } from 'react';
import cx from 'classnames';

import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { useLazyComponent } from '@trello/use-lazy-component';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import { SingleBoardViewProvider } from 'app/src/components/BoardViewContext/SingleBoardViewProvider';
import { SingleBoardViewFiltersProvider } from 'app/src/components/ViewFilters/SingleBoardViewFiltersProvider';

import styles from './SingleBoardCalendarView.less';

interface SingleBoardCalendarViewProps {
  idBoard: string;
  navigateToCard: (id: string) => void;
}

export const SingleBoardCalendarView: FunctionComponent<
  SingleBoardCalendarViewProps
> = ({ idBoard, navigateToCard }: SingleBoardCalendarViewProps) => {
  const CalendarView = useLazyComponent(
    () => import(/* webpackChunkName: "calendar-view" */ './CalendarView'),
    { namedImport: 'CalendarView' },
  );

  const [{ expanded: workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();

  return (
    <div
      className={cx(styles.container, {
        [styles.collapsedWorkspaceNavigation]:
          !workspaceNavigationHidden && !workspaceNavigationExpanded,
      })}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-workflowers',
          feature: 'Calendar View',
        }}
      >
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={null}>
            <SingleBoardViewFiltersProvider idBoard={idBoard}>
              <SingleBoardViewProvider
                idBoard={idBoard}
                isDateBasedView={true}
                navigateToCard={navigateToCard}
                includeChecklistItems={true}
              >
                <CalendarView />
              </SingleBoardViewProvider>
            </SingleBoardViewFiltersProvider>
          </ChunkLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};
